import { useCallback, useEffect, useRef } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import {
  isTerminalRunStatus,
  type OperationRun,
  type OperationRunDetail,
  type OperationRunNode,
} from "../lib/operationRun.js";

// ADR-0128 — Hub Execution Isolation. Shared hook that every `execute-preview`
// caller uses to drive the async run API:
//
//   POST /operations/:id/nodes/:nodeId/execute-preview  → 202 { runId }
//                                                        → 409 { runId } (attach)
//   poll GET /operations/:id/runs/:runId every 1s until run.status is terminal
//     succeeded → onComplete(run, nodes) — caller then re-reads the node cache
//     failed    → onError(run.error, run)
//     cancelled → onCancelled(run) (settle quietly)
//
// The run response carries no rows: actual row/preview data is still read from
// the node cache by the caller inside onComplete (unchanged from the old
// post-receipt flow). Polling is torn down on unmount so nothing outlives the
// editor.

const POLL_INTERVAL_MS = 1000;

export type StartRunOptions = {
  operationId: string;
  nodeId: string;
  forceRefresh?: boolean;
  /** Aborts the poll loop (e.g. inspector unmount). Does NOT cancel the run. */
  signal?: AbortSignal;
  /** Fires once the run reaches `queued`/`running` with the resolved runId. */
  onStart?: (runId: string, attached: boolean) => void;
  /** Fires on every poll tick with the latest run + node snapshot. */
  onProgress?: (run: OperationRun, nodes: OperationRunNode[]) => void;
  /** Terminal `succeeded`. Caller re-reads the node cache here. */
  onComplete: (run: OperationRun, nodes: OperationRunNode[]) => void | Promise<void>;
  /** Terminal `failed`. `error` is `run.error` (may be null). */
  onError?: (error: string | null, run: OperationRun) => void;
  /** Terminal `cancelled`. Settle quietly (no error surface). */
  onCancelled?: (run: OperationRun) => void;
  /** Non-run failures: POST rejected, network error, poll fetch failed. */
  onFailure?: (err: unknown) => void;
};

export type OperationRunController = {
  /**
   * Kick off (or attach to) a run for a node. Resolves with the runId once the
   * run is settled or the poll loop is torn down; resolves with null if the
   * POST failed before a run could be resolved.
   */
  startRun: (opts: StartRunOptions) => Promise<string | null>;
  /** Request cancellation of an in-flight run via the cancel route. */
  cancelRun: (operationId: string, runId: string) => Promise<void>;
};

async function fetchRunDetail(
  operationId: string,
  runId: string,
  signal?: AbortSignal
): Promise<OperationRunDetail> {
  return apiFetch<OperationRunDetail>(
    `/api/tenant/operations/${encodeURIComponent(operationId)}/runs/${encodeURIComponent(runId)}`,
    { signal }
  );
}

export function useOperationRun(): OperationRunController {
  // Poll timers by runId so we can clear them on unmount.
  const timersRef = useRef<Map<string, number>>(new Map());
  const disposedRef = useRef(false);

  useEffect(() => {
    // React 18 StrictMode mounts → unmounts → remounts in dev. The cleanup
    // below marks the hook disposed; without this reset the remounted hook
    // stays permanently "disposed" and the poll loop settles before its first
    // fetch — the run completes server-side but the UI never hears about it.
    disposedRef.current = false;
    const timers = timersRef.current;
    return () => {
      disposedRef.current = true;
      for (const id of timers.values()) window.clearTimeout(id);
      timers.clear();
    };
  }, []);

  const startRun = useCallback(async (opts: StartRunOptions): Promise<string | null> => {
    const { operationId, nodeId, forceRefresh, signal } = opts;

    // 1. POST → 202 { runId } (fresh) | 409 { runId } (attach to active run).
    let runId: string;
    let attached = false;
    try {
      const res = await apiFetch<{ runId: string }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/execute-preview`,
        { method: "POST", body: { forceRefresh: forceRefresh ?? false }, signal }
      );
      runId = res.runId;
    } catch (e) {
      // 409 run_in_progress → the returned runId IS the already-active run;
      // attach to it (poll it) rather than surfacing an error.
      if (e instanceof ApiRequestError && e.status === 409 && e.code === "run_in_progress") {
        const existing = e.data?.runId;
        if (typeof existing === "string" && existing) {
          runId = existing;
          attached = true;
        } else {
          opts.onFailure?.(e);
          return null;
        }
      } else if (e instanceof DOMException && e.name === "AbortError") {
        return null;
      } else {
        opts.onFailure?.(e);
        return null;
      }
    }

    opts.onStart?.(runId, attached);

    // 2. Poll GET /runs/:runId every 1s until terminal.
    return new Promise<string | null>((resolve) => {
      const settle = (value: string | null) => {
        const t = timersRef.current.get(runId);
        if (t !== undefined) {
          window.clearTimeout(t);
          timersRef.current.delete(runId);
        }
        resolve(value);
      };

      const tick = async (): Promise<void> => {
        if (disposedRef.current || signal?.aborted) {
          settle(runId);
          return;
        }
        let detail: OperationRunDetail;
        try {
          detail = await fetchRunDetail(operationId, runId, signal);
        } catch (e) {
          if (e instanceof DOMException && e.name === "AbortError") {
            settle(runId);
            return;
          }
          // Transient poll failure — back off and retry unless torn down.
          if (disposedRef.current || signal?.aborted) {
            settle(runId);
            return;
          }
          const t = window.setTimeout(() => void tick(), POLL_INTERVAL_MS * 3);
          timersRef.current.set(runId, t);
          return;
        }
        if (disposedRef.current || signal?.aborted) {
          settle(runId);
          return;
        }

        const { run, nodes } = detail;
        opts.onProgress?.(run, nodes);

        if (isTerminalRunStatus(run.status)) {
          try {
            if (run.status === "succeeded") {
              await opts.onComplete(run, nodes);
            } else if (run.status === "failed") {
              opts.onError?.(run.error, run);
            } else if (run.status === "cancelled") {
              opts.onCancelled?.(run);
            }
          } finally {
            settle(runId);
          }
          return;
        }

        const t = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
        timersRef.current.set(runId, t);
      };

      void tick();
    });
  }, []);

  const cancelRun = useCallback(async (operationId: string, runId: string): Promise<void> => {
    await apiFetch(
      `/api/tenant/operations/${encodeURIComponent(operationId)}/runs/${encodeURIComponent(runId)}/cancel`,
      { method: "POST" }
    );
  }, []);

  return { startRun, cancelRun };
}
